import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import RemoveIcon from '@mui/icons-material/Remove'
import { Box, Button, Collapse, Divider, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material'
import { useState } from 'react'

import { useAvisosCocina } from '../contexts/avisosCocinaContext.ts'
import { useAhora } from '../hooks/useAhora.ts'
import AvisoCocinaItem from './AvisoCocinaItem.tsx'
import AvisosCocinaBell from './AvisosCocinaBell.tsx'

/** Avisos mostrados sin expandir la pila */
const MAX_PLEGADO = 3

/**
 * Pila de avisos de cocina: discreta, abajo a la izquierda, por debajo de diálogos y toasts.
 * Persiste hasta que el aviso se marca como entregado. Al minimizarla queda un botón flotante
 * con el contador; un aviso nuevo la vuelve a abrir.
 */
const AvisosCocinaPila = () => {
  const { avisos, visibles, procesando, entregar, minimizar } = useAvisosCocina()
  const ahora = useAhora()
  const [expandida, setExpandida] = useState(false)

  if (avisos.length === 0) return null
  if (visibles.length === 0) return <AvisosCocinaBell flotante />

  const primeros = visibles.slice(0, MAX_PLEGADO)
  const resto = visibles.slice(MAX_PLEGADO)

  return (
    <Paper
      elevation={6}
      role="region"
      aria-label="Pedidos listos en cocina"
      sx={{
        position: 'fixed',
        left: 16,
        bottom: 'max(16px, env(safe-area-inset-bottom, 0px))',
        // Debajo de diálogos (1300) y toasts; encima del contenido y la barra superior
        zIndex: 1250,
        width: { xs: 'calc(100% - 32px)', sm: 360 },
        borderLeft: '4px solid',
        borderLeftColor: 'success.main',
        overflow: 'hidden',
      }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', px: 1.5, py: 0.75, bgcolor: 'action.hover' }}>
        <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 600 }}>
          Pedidos listos ({visibles.length})
        </Typography>
        <Tooltip title="Minimizar">
          <IconButton size="small" onClick={minimizar} aria-label="minimizar avisos">
            <RemoveIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Divider />
      <Stack sx={{ px: 1.5, py: 1, gap: 1 }}>
        {primeros.map((aviso) => (
          <AvisoCocinaItem
            key={aviso.key}
            aviso={aviso}
            ahora={ahora}
            ocupado={procesando.has(aviso.key)}
            onEntregar={(a) => void entregar(a)}
          />
        ))}
      </Stack>
      {resto.length > 0 && (
        <>
          <Collapse in={expandida} unmountOnExit>
            <Stack sx={{ px: 1.5, pb: 1, gap: 1, maxHeight: 260, overflowY: 'auto' }}>
              {resto.map((aviso) => (
                <AvisoCocinaItem
                  key={aviso.key}
                  aviso={aviso}
                  ahora={ahora}
                  ocupado={procesando.has(aviso.key)}
                  onEntregar={(a) => void entregar(a)}
                />
              ))}
            </Stack>
          </Collapse>
          <Box sx={{ px: 1, pb: 0.5 }}>
            <Button
              size="small"
              fullWidth
              onClick={() => setExpandida((v) => !v)}
              endIcon={expandida ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              {expandida ? 'Ver menos' : `+${resto.length} más`}
            </Button>
          </Box>
        </>
      )}
    </Paper>
  )
}

export default AvisosCocinaPila
